import { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Main as Container } from 'Components/Main';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { getAreaById, archiveArea, unarchiveArea } from 'services/areas-service';
import { Dialog, PageHeader } from 'Components/ui';
import styled from 'styled-components';
import { formatDate as formatDateIntl, LOCALE_MAP } from 'utils/locale-formatting';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';
const BORDER = 'var(--vo-line)';
const DIVIDER = 'var(--vo-cloud)';
const BG = 'var(--vo-mist)';
const TEXT = 'var(--vo-ink-2)';

const EditBtn = styled.button`
  height: 40px;
  background: white;
  color: ${BLUE};
  border: 1.5px solid ${BLUE};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 6px;
`;
const ArchiveBtn = styled.button`
  height: 40px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TabsRow = styled.div`
  display: flex;
  background: white;
  width: fit-content;
  border-radius: 12px;
  padding: 5px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 20px;
`;
const TabBtn = styled.button`
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 9px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '600' : '500'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active }) => $active ? BLUE : 'transparent'};
  color: ${({ $active }) => $active ? 'white' : MUTED};
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
`;
const InfoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  box-sizing: border-box;
`;
const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${DIVIDER};
`;
const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
`;
const FieldBlock = styled.div`
  ${({ $full }) => $full && 'grid-column: 1 / -1;'}
`;
const FieldLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 6px;
`;
const CritBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-block;
`;
const AvatarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Rubik', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
`;
const AvatarName = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${INK};
  font-weight: 500;
`;
const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const TagChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${BLUE};
  background: var(--vo-capri-50);
  padding: 3px 12px;
  border-radius: 20px;
`;
const DescText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${TEXT};
  margin: 0;
  line-height: 1.65;
`;
const EmptyVal = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const SideCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const SideCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${DIVIDER};
`;
const SideFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const SideValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  margin: 0;
`;
const RelatedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const RelatedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: ${BG};
  border-radius: 10px;
`;
const RelatedLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  color: ${TEXT};
  font-weight: 500;
`;
const RelatedCount = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  color: ${BLUE};
  font-weight: 700;
`;

const BondsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  align-items: start;
`;
const VinculoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const VinculoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${DIVIDER};
`;
const VinculoTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;
const AddBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: var(--vo-capri-50);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${BLUE};
  font-size: 20px;
  line-height: 1;
  flex-shrink: 0;
  padding: 0 0 2px 0;
`;
const VinculoChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const VinculoChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${BLUE_DEEP};
  background: var(--vo-cobalto-8);
  padding: 6px 14px;
  border-radius: 20px;
`;

const getCriticityInfo = (level, t) => {
  const map = {
    '1': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
    '2': { color: BLUE,      bg: 'var(--vo-info-10)' },
    '3': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
    '4': { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
    '5': { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
  };
  const styles = map[String(level)] || { color: MUTED, bg: BG };
  const short = t(`asm:criticality.short.${level}`, '');
  return { ...styles, label: short ? `${short} (${level})` : String(level ?? '—') };
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const AreasShow = () => {
  const { t, i18n } = useTranslation(['asm']);
  const navigate = useNavigate();
  const { id } = useParams();
  const [tabPosition, setTabPosition] = useState(0);
  const locale = LOCALE_MAP[i18n.language] ?? 'pt-BR';
  const formatDate = (dateStr) => formatDateIntl(dateStr, locale) || '—';

  const { isLoading, error, data: area, refetch } = useQuery(['getAreaById', id], async () => {
    const dataArea = await getAreaById(id);
    return dataArea.status === 200 ? dataArea.data : null;
  });

  const onClickEdit = () => navigate('/areas/edit/' + id);

  const onClickArchive = async () => {
    const isArchived = area?.archived;
    const confirmKey = isArchived ? 'asm:areas.delete.unarchive' : 'asm:areas.delete.archive';
    const actionLabel = isArchived ? t('asm:areas.show.unarchive') : t('asm:areas.show.archive');

    const confirmed = await Dialog.confirm({
      title: `${t(confirmKey)} "${area?.name}"?`,
      confirmLabel: actionLabel,
      cancelLabel: t('asm:areas.form.cancel'),
    });
    if (!confirmed) return;

    try {
      const res = isArchived ? await unarchiveArea(id) : await archiveArea(id);
      if (res.status === 200) {
        await refetch();
        Dialog.fire({ icon: 'success', title: 'Sucesso!', timer: 1500, showConfirmButton: false });
      }
    } catch {
      Dialog.alert({ icon: 'error', title: t('asm:areas.delete.errorTitle') });
    }
  };

  const critInfo = getCriticityInfo(area?.criticity, t);

  return (
    <Container>
      {isLoading && <PageLoaderShow />}
      {error && <div style={{ padding: 32 }}>{t('asm:areas.show.error')}</div>}
      {!isLoading && !error && area && (
        <>
          <PageHeader
            title={area.name}
            segments={[
              { label: t('common:breadcrumbs.mapping') },
              { label: t('asm:areas.title'), route: '/areas' },
              { label: area.name },
            ]}
            actions={
              <>
                <EditBtn onClick={onClickEdit}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  {t('asm:areas.show.edit')}
                </EditBtn>
                <ArchiveBtn onClick={onClickArchive}>
                  {area.archived ? t('asm:areas.show.unarchive') : t('asm:areas.show.archive')}
                </ArchiveBtn>
              </>
            }
          />

          <TabsRow>
            <TabBtn $active={tabPosition === 0} onClick={() => setTabPosition(0)}>
              {t('asm:areas.show.tabDetails')}
            </TabBtn>
            <TabBtn $active={tabPosition === 1} onClick={() => setTabPosition(1)}>
              {t('asm:areas.show.tabBonds')}
            </TabBtn>
          </TabsRow>

          {tabPosition === 0 && (
            <MainGrid>
              <InfoCard>
                <CardTitle>{t('asm:areas.show.infoSectionTitle')}</CardTitle>
                <FieldsGrid>
                  <FieldBlock>
                    <FieldLabel>{t('asm:areas.show.criticality')}</FieldLabel>
                    <CritBadge style={{ color: critInfo.color, background: critInfo.bg }}>
                      {critInfo.label}
                    </CritBadge>
                  </FieldBlock>

                  <FieldBlock>
                    <FieldLabel>{t('asm:areas.show.responsible')}</FieldLabel>
                    <AvatarRow>
                      <Avatar>{getInitials(area.user)}</Avatar>
                      <AvatarName>{area.user || '—'}</AvatarName>
                    </AvatarRow>
                  </FieldBlock>

                  <FieldBlock $full>
                    <FieldLabel>{t('asm:areas.show.tags')}</FieldLabel>
                    <ChipsRow>
                      {area.tags?.length
                        ? area.tags.map(tag => <TagChip key={tag}>{tag}</TagChip>)
                        : <EmptyVal>—</EmptyVal>}
                    </ChipsRow>
                  </FieldBlock>

                  <FieldBlock $full>
                    <FieldLabel>{t('asm:areas.show.description')}</FieldLabel>
                    <DescText>{area.description || '—'}</DescText>
                  </FieldBlock>
                </FieldsGrid>
              </InfoCard>

              <Sidebar>
                <SideCard>
                  <SideCardTitle>{t('asm:areas.show.historySectionTitle')}</SideCardTitle>
                  <SideFields>
                    <div>
                      <FieldLabel>{t('asm:areas.show.createdAt')}</FieldLabel>
                      <SideValue>{formatDate(area.createdAt)}</SideValue>
                    </div>
                    <div>
                      <FieldLabel>{t('asm:areas.show.updatedAt')}</FieldLabel>
                      <SideValue>{formatDate(area.updatedAt)}</SideValue>
                    </div>
                  </SideFields>
                </SideCard>

                <SideCard>
                  <SideCardTitle>{t('asm:areas.show.relatedDataTitle')}</SideCardTitle>
                  <RelatedList>
                    <RelatedRow>
                      <RelatedLabel>{t('asm:areas.form.bonds.processes')}</RelatedLabel>
                      <RelatedCount>{area.processes?.filter(p => !p.archived).length ?? 0}</RelatedCount>
                    </RelatedRow>
                    <RelatedRow>
                      <RelatedLabel>{t('asm:areas.form.bonds.systems')}</RelatedLabel>
                      <RelatedCount>{area.systems?.filter(s => !s.archived).length ?? 0}</RelatedCount>
                    </RelatedRow>
                    <RelatedRow>
                      <RelatedLabel>{t('asm:areas.form.bonds.assets')}</RelatedLabel>
                      <RelatedCount>{area.assets?.filter(a => !a.archived).length ?? 0}</RelatedCount>
                    </RelatedRow>
                  </RelatedList>
                </SideCard>
              </Sidebar>
            </MainGrid>
          )}

          {tabPosition === 1 && (
            <BondsGrid>
              <VinculoCard>
                <VinculoHeader>
                  <VinculoTitle>{t('asm:areas.form.bonds.processes')}</VinculoTitle>
                  <AddBtn type="button" onClick={onClickEdit}>+</AddBtn>
                </VinculoHeader>
                <VinculoChips>
                  {area.processes?.filter(p => !p.archived).map(p => (
                    <VinculoChip key={p._id || p.name}>{p.name}</VinculoChip>
                  ))}
                </VinculoChips>
              </VinculoCard>

              <VinculoCard>
                <VinculoHeader>
                  <VinculoTitle>{t('asm:areas.form.bonds.systems')}</VinculoTitle>
                  <AddBtn type="button" onClick={onClickEdit}>+</AddBtn>
                </VinculoHeader>
                <VinculoChips>
                  {area.systems?.filter(s => !s.archived).map(s => (
                    <VinculoChip key={s._id || s.name}>{s.name}</VinculoChip>
                  ))}
                </VinculoChips>
              </VinculoCard>

              <VinculoCard>
                <VinculoHeader>
                  <VinculoTitle>{t('asm:areas.form.bonds.assets')}</VinculoTitle>
                  <AddBtn type="button" onClick={onClickEdit}>+</AddBtn>
                </VinculoHeader>
                <VinculoChips>
                  {area.assets?.filter(a => !a.archived).map(a => (
                    <VinculoChip key={a._id || a.name}>{a.name}</VinculoChip>
                  ))}
                </VinculoChips>
              </VinculoCard>
            </BondsGrid>
          )}
        </>
      )}
    </Container>
  );
};
